import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { useNavigate, useOutletContext } from "react-router-dom";
import type { DateRange, SortOption } from "../../features/statsNew/components";
import { getSessionDate, isSessionInDateRange } from "../../features/statsNew/utils/dateUtils";
import {
  SkeletonCard,
  EmptyState,
  SearchAndSort,
  Pagination,
} from "../../features/statsNew/components";
import {
  getAllSessionsByProjectGroup,
  sessionsByProjectGroupSelector,
} from "../../features/stats/state/statsSlice";
import {
  fetchProjectGroupsWithProjects,
  getAllProjectGroupsWithNestedElements,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { setDateRange } from "../../features/statsNew/state/statsNewSlice";
import { useCreatorStatsCache } from "../../features/statsNew/hooks";
import { STATS_FETCH_ALL_LIMIT } from "../../features/statsNew/constants";

interface OutletContextType {
  dateRange: DateRange;
}

interface ParcoursData {
  id: number;
  name: string;
  sessionCount: number;
  lastSessionDate: Date | null;
  firstSessionDate: Date | null;
  createdAt: Date | null;
  moduleCount: number;
  isDeleted?: boolean;
  thumbnailUrl: string | null;
}

const getSessionBadgeColor = (sessionCount: number): string => {
  if (sessionCount <= 5) return "bg-gray-400";
  if (sessionCount <= 50) return "bg-green-500";
  return "bg-green-600";
};

const ParcoursListView = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { dateRange } = useOutletContext<OutletContextType>();
  const { shouldRefetch, markAsFetched } = useCreatorStatsCache();

  const sessionsByProjectGroup = useAppSelector(sessionsByProjectGroupSelector);
  const projectGroups = useAppSelector(getAllProjectGroupsWithNestedElements);

  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [sortKey, setSortKey] = useState("activity-desc");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showDeleted, setShowDeleted] = useState(false);

  const sortOptions: SortOption[] = [
    // Par activité
    { key: "activity-desc", label: t("statsNew.sort.activityRecent") },
    { key: "activity-asc", label: t("statsNew.sort.activityOldest") },
    { key: "sessions-desc", label: t("statsNew.sort.sessionsMost") },
    { key: "sessions-asc", label: t("statsNew.sort.sessionsLeast") },
    // Par date
    { key: "firstSession-desc", label: t("statsNew.sort.firstSessionRecent") },
    { key: "firstSession-asc", label: t("statsNew.sort.firstSessionOldest") },
    { key: "created-desc", label: t("statsNew.sort.createdRecent") },
    { key: "created-asc", label: t("statsNew.sort.createdOldest") },
    // Par nom
    { key: "name-asc", label: t("statsNew.sort.nameAZ") },
    { key: "name-desc", label: t("statsNew.sort.nameZA") },
  ];

  // Fetch only if cache is invalid (org changed, first load, or visibility refresh)
  useEffect(() => {
    if (shouldRefetch) {
      setIsLoading(true);
      void Promise.all([
        dispatch(getAllSessionsByProjectGroup({ componentId: "statsNewParcoursListView" })),
        dispatch(
          fetchProjectGroupsWithProjects({
            componentId: "statsNewParcoursListView",
            limit: STATS_FETCH_ALL_LIMIT,
          }),
        ),
      ]).then(() => {
        markAsFetched();
        setIsLoading(false);
      });
    }
  }, [dispatch, shouldRefetch, markAsFetched]);

  // Show loading state while fetching
  const showSkeleton = isLoading || (shouldRefetch && sessionsByProjectGroup.length === 0);

  // Legacy tracking (kept for compatibility but now using showSkeleton)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (sessionsByProjectGroup.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [sessionsByProjectGroup, isInitialLoad]);

  const handleClearDateRange = () => {
    dispatch(setDateRange({ startDate: null, endDate: null }));
  };

  // Merge stats data with project group data (for thumbnails)
  const parcoursData = useMemo<ParcoursData[]>(() => {
    return sessionsByProjectGroup.map((group) => {
      let sessionCount = 0;
      let lastSessionDate: Date | null = null;
      let firstSessionDate: Date | null = null;

      group.projects.forEach((project) => {
        project.sessions.forEach((session) => {
          const sessionDate = getSessionDate(session);

          // Apply date range filter
          if (!isSessionInDateRange(sessionDate, dateRange)) return;

          sessionCount++;
          if (!lastSessionDate || sessionDate > lastSessionDate) {
            lastSessionDate = sessionDate;
          }
          if (!firstSessionDate || sessionDate < firstSessionDate) {
            firstSessionDate = sessionDate;
          }
        });
      });

      // Find thumbnail and createdAt from projectGroups
      const pgData = projectGroups.find((pg) => pg.id === group.project_group_id);
      const thumbnailUrl = pgData?.source_thumbnail?.s3_url || null;
      const createdAt = pgData?.createdAt ? new Date(pgData.createdAt) : null;

      return {
        id: group.project_group_id,
        name: group.project_group_name,
        sessionCount,
        lastSessionDate,
        firstSessionDate,
        createdAt,
        moduleCount: group.projects.length,
        isDeleted: group.is_deleted,
        thumbnailUrl,
      };
    });
  }, [sessionsByProjectGroup, dateRange, projectGroups]);

  const filteredAndSortedData = useMemo(() => {
    // Filter out parcours with 0 sessions in the date range
    let result = parcoursData.filter((p) => p.sessionCount > 0);

    // Filter out deleted parcours if showDeleted is false
    if (!showDeleted) {
      result = result.filter((p) => !p.isDeleted);
    }

    // Filter by search
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(search));
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortKey) {
        case "sessions-desc":
          return b.sessionCount - a.sessionCount;
        case "sessions-asc":
          return a.sessionCount - b.sessionCount;
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "activity-desc":
          if (!a.lastSessionDate) return 1;
          if (!b.lastSessionDate) return -1;
          return b.lastSessionDate.getTime() - a.lastSessionDate.getTime();
        case "activity-asc":
          if (!a.lastSessionDate) return 1;
          if (!b.lastSessionDate) return -1;
          return a.lastSessionDate.getTime() - b.lastSessionDate.getTime();
        case "firstSession-desc":
          if (!a.firstSessionDate) return 1;
          if (!b.firstSessionDate) return -1;
          return b.firstSessionDate.getTime() - a.firstSessionDate.getTime();
        case "firstSession-asc":
          if (!a.firstSessionDate) return 1;
          if (!b.firstSessionDate) return -1;
          return a.firstSessionDate.getTime() - b.firstSessionDate.getTime();
        case "created-desc":
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return b.createdAt.getTime() - a.createdAt.getTime();
        case "created-asc":
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return a.createdAt.getTime() - b.createdAt.getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [parcoursData, searchValue, sortKey, showDeleted]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchValue, sortKey, dateRange, showDeleted]);

  // Paginate the data
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredAndSortedData.slice(start, start + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleParcoursClick = (parcoursId: number) => {
    navigate(`/statistics/parcours/${parcoursId}`);
  };

  // Show skeleton during loading
  if (showSkeleton) {
    return (
      <div className="flex flex-wrap gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <SkeletonCard key={i} variant="parcours" />
        ))}
      </div>
    );
  }

  // Show empty state based on context (but keep search visible for search-related empty states)
  if (filteredAndSortedData.length === 0) {
    // No results due to search - keep SearchAndSort visible
    if (searchValue) {
      return (
        <div className="flex flex-col h-full">
          <div className="flex-shrink-0 mb-4 flex items-center gap-4">
            <SearchAndSort
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              searchPlaceholder={t("statsNew.parcours.searchPlaceholder")}
              sortOptions={sortOptions}
              selectedSort={sortKey}
              onSortChange={setSortKey}
            />
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
              />
              {t("statsNew.filters.showDeleted")}
            </label>
          </div>
          <EmptyState
            icon="search"
            title={t("statsNew.emptyState.noSearchResults")}
            description={t("statsNew.emptyState.noSearchResultsDescription")}
          />
        </div>
      );
    }

    // No results due to date range filter
    if (dateRange.startDate || dateRange.endDate) {
      return (
        <EmptyState
          icon="calendar"
          title={t("statsNew.emptyState.noParcours")}
          description={t("statsNew.emptyState.noParcoursDescription")}
          action={{
            label: t("statsNew.emptyState.viewAll"),
            onClick: handleClearDateRange,
          }}
        />
      );
    }

    // No data at all
    return (
      <EmptyState
        icon="folder"
        title={t("statsNew.emptyState.noParcours")}
        description={t("statsNew.emptyState.noParcoursDescription")}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search, Sort and Filters */}
      <div className="flex-shrink-0 mb-4 flex items-center gap-4">
        <SearchAndSort
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t("statsNew.parcours.searchPlaceholder")}
          sortOptions={sortOptions}
          selectedSort={sortKey}
          onSortChange={setSortKey}
        />
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
          />
          {t("statsNew.filters.showDeleted")}
        </label>
      </div>

      {/* Parcours grid */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="flex flex-wrap gap-4">
          {paginatedData.map((parcours) => (
            <div
              key={parcours.id}
              onClick={() => handleParcoursClick(parcours.id)}
              className="relative group cursor-pointer rounded-xl overflow-hidden shadow hover:shadow-lg transition-all hover:scale-[1.02]"
              style={{ width: "180px", height: "120px" }}
            >
              {/* Background image or gradient */}
              {parcours.thumbnailUrl ? (
                <img
                  src={parcours.thumbnailUrl}
                  alt={parcours.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800">
                  {/* Subtle decorative pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-2 right-8 w-12 h-12 border-2 border-white rounded-full" />
                    <div className="absolute bottom-8 left-3 w-6 h-6 border-2 border-white rounded-full" />
                  </div>
                </div>
              )}

              {/* Dark overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

              {/* Content */}
              <div className="absolute inset-0 p-3 flex flex-col justify-end">
                {/* Name */}
                <h3
                  className="text-white font-semibold text-sm leading-tight line-clamp-2"
                  title={parcours.name}
                >
                  {parcours.name}
                </h3>
              </div>

              {/* Session count badge */}
              <div className="absolute top-2 right-2 transition-transform">
                <span
                  className={`inline-flex items-center justify-center min-w-[1.75rem] h-6 px-1.5 ${getSessionBadgeColor(
                    parcours.sessionCount,
                  )} text-white text-xs font-bold rounded-lg shadow`}
                >
                  {parcours.sessionCount} {t("statsNew.session.sessions")}
                </span>
              </div>

              {/* Deleted indicator */}
              {parcours.isDeleted && (
                <div className="absolute top-2 left-2">
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-medium rounded">
                    {t("statsNew.parcours.deleted")}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {filteredAndSortedData.length > 0 && (
        <div className="flex-shrink-0 mt-auto">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredAndSortedData.length}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}
    </div>
  );
};

export default ParcoursListView;
